/**
 * Slot-chip helpers for the discovery cards (design-spec §5 key behaviours 1–2).
 *
 * - Day labels are RELATIVE ("Today", "Tomorrow", then the weekday) and are
 *   computed in the SALON's timezone, never the browser's: a Lisbon salon's
 *   "today" at 23:30 Lisbon time is still today even if the visitor's clock in
 *   Brussels already says tomorrow.
 * - The first chip is "soonest" only when it is today or tomorrow (board 02:
 *   a Friday-only row has no highlight).
 * - Every chip deep-links into the booking time step with the slot preselected.
 *
 * No timezone library: everything is derived from Intl (see slot-timezone.ts).
 */
import { isValidTimeZone } from './slot-timezone';

export type RelativeDayKind = 'today' | 'tomorrow' | 'week' | 'later';

export interface RelativeDay {
  kind: RelativeDayKind;
  /** Whole days from the salon's today to `date` (negative = in the past). */
  diff: number;
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function utcDay(date: string): number | null {
  const m = DATE_RE.exec(date);
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** "YYYY-MM-DD" for `now` as seen on a wall clock in `timeZone` (falls back to the runtime zone). */
export function todayInZone(timeZone: string | null | undefined, now: Date = new Date()): string {
  const opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
  if (isValidTimeZone(timeZone)) opts.timeZone = timeZone;
  const parts = new Intl.DateTimeFormat('en-US', opts).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * The ONE day classification for every slot label (cards, profile, book card):
 * 0 → today, 1 → tomorrow, 2–6 → weekday, else (≥ 7, or a stale date in the
 * past) → "later", i.e. the full date. A past date is never called "Today".
 */
export function dayKind(diff: number): RelativeDayKind {
  if (!Number.isFinite(diff) || diff < 0) return 'later';
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff < 7) return 'week';
  return 'later';
}

/** Soonest highlight: only the first chip, and only when it is today or tomorrow. */
export function isSoonestKind(index: number, kind: RelativeDayKind): boolean {
  return index === 0 && (kind === 'today' || kind === 'tomorrow');
}

/** Where a salon-local `date` sits relative to the salon's today. */
export function relativeDay(
  date: string,
  timeZone: string | null | undefined,
  now: Date = new Date(),
): RelativeDay {
  const target = utcDay(date);
  const today = utcDay(todayInZone(timeZone, now));
  if (target == null || today == null) return { kind: 'later', diff: Number.NaN };
  const diff = Math.round((target - today) / 86_400_000);
  return { kind: dayKind(diff), diff };
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Human label for a slot day.
 * - `short` (card chips): "Today", "Tomorrow", "Fri", "Fri 9 Oct"
 * - `long`  (result-row day label): "Today", "Tomorrow", "Friday", "Friday 9 October"
 *
 * Weekday names come from Intl in the UI locale; "Today"/"Tomorrow" from i18n.
 */
export function dayLabel(
  date: string,
  timeZone: string | null | undefined,
  t: Translate,
  locale: string,
  { now = new Date(), style = 'short' }: { now?: Date; style?: 'short' | 'long' } = {},
): string {
  const rel = relativeDay(date, timeZone, now);
  if (rel.kind === 'today') return t('slots.today');
  if (rel.kind === 'tomorrow') return t('slots.tomorrow');
  const day = utcDay(date);
  if (day == null) return date;
  const weekday = style === 'long' ? 'long' : 'short';
  const fmt = (o: Intl.DateTimeFormatOptions) => {
    try {
      return new Intl.DateTimeFormat(locale, { ...o, timeZone: 'UTC' }).format(new Date(day));
    } catch {
      return new Intl.DateTimeFormat('en', { ...o, timeZone: 'UTC' }).format(new Date(day));
    }
  };
  if (rel.kind === 'week') return fmt({ weekday });
  return fmt({ weekday, day: 'numeric', month: style === 'long' ? 'long' : 'short' });
}

/** The first chip gets the soonest style only when it is today or tomorrow. */
export function isSoonest(index: number, date: string, timeZone: string | null | undefined, now: Date = new Date()): boolean {
  if (index !== 0) return false;
  return isSoonestKind(index, relativeDay(date, timeZone, now).kind);
}

/**
 * Deep link into the booking time step with the slot preselected:
 * `/salon/:subdomain/book/time?service=<id>&date=<YYYY-MM-DD>&time=<HH:mm>`.
 * (The route lands in M4; until then app.tsx redirects it to the salon page.)
 */
export function slotHref(subdomain: string, serviceId: string, date: string, time: string): string {
  const qs = new URLSearchParams({ service: serviceId, date, time });
  return `/salon/${encodeURIComponent(subdomain)}/book/time?${qs.toString()}`;
}

/** "Book now" entry: the service step. */
export function bookServiceHref(subdomain: string): string {
  return `/salon/${encodeURIComponent(subdomain)}/book/service`;
}

export function salonHref(subdomain: string): string {
  return `/salon/${encodeURIComponent(subdomain)}`;
}
